import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { A as ArrowRight, d as defineCustomElement$3 } from './g-button2.js';
import { d as defineCustomElement$2 } from './g-loader2.js';

const gInputCss = ".g__input__label__text--hidden{position:absolute;overflow:hidden;clip:rect(0 0 0 0);height:1px;width:1px;margin:-1px;padding:0;border:0}.g__input{box-sizing:border-box;display:block;}.g__input__wrapper{display:flex;flex-direction:row;align-items:center}.g__input__label{display:block;width:100%}.g__input__label__details{display:inline-block}.g__input__label__summary{font-weight:700;display:inline-flex;align-items:center;list-style-type:none;cursor:pointer}.g__input__label__summary::-webkit-details-marker{display:none}.g__input__label__summary__icon{margin-left:0.5rem;width:1em;height:1em}.g__input__label__summary__copy{font-weight:400;display:block}.g__input__label__text{font-weight:700;font-size:0.9em;text-align:left}.g__input__required{color:#920000;padding-left:0.25rem}.g__input .g__input__wrapper{margin:0.3rem 0 0.3rem 0}.g__input input{display:block;border:1px solid #d9d9d9;border-radius:0;font-size:1em;line-height:1.15;padding:0.875em;width:100%}.g__input input:not(:disabled):focus,.g__input input:not(:disabled):active{border-color:#000;outline:none}.g__input input:disabled{cursor:not-allowed}.g__input--error input{border:1px solid #920000}.g__input--with-button input{height:2.5rem;padding:0.625rem 0.5rem}.g__input--with-floating-label .g__input__wrapper{align-items:stretch;border:1px solid #d9d9d9;white-space:nowrap}.g__input--with-floating-label input{border:none}.g__input--with-floating-label:focus-within .g__input__wrapper{border-color:#000}.g__input--with-floating-label button{background:none;border:none;cursor:pointer;padding:0 0.875em}.g__input--with-floating-label--inactive button{color:#737373}.g__input--with-floating-label--loading .g__input__wrapper{align-items:center;padding-right:0.875em}.g__input--error.g__input--with-floating-label .g__input__wrapper{border-color:#920000}.g__input--error.g__input--with-floating-label:focus-within .g__input__wrapper{border-color:#000}.g__input input[type=number]::-webkit-inner-spin-button,.g__input input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}.g__input input[type=number]{-moz-appearance:textfield}.g__input__button-wrapper .g__button__host{margin-bottom:0}.g__input__button-wrapper button{margin-bottom:0;padding:0.563rem;height:2.5rem}.g__input__button-wrapper .g__button--contained--dark{border:1px solid #000}.g__input__button--append__icon svg{height:0.9em;width:1.4em;display:inline-block;vertical-align:middle;fill:currentcolor}";
const GInputStyle0 = gInputCss;

const GInput$1 = /*@__PURE__*/ proxyCustomElement(class GInput extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.gOpen = createEvent(this, "g-open", 7);
        this.gClose = createEvent(this, "g-close", 7);
        this.gClickAppend = createEvent(this, "g-click-append", 7);
        this.gClickFloatingLabel = createEvent(this, "g-click-floating-label", 7);
        this.gValid = createEvent(this, "g-valid", 7);
        this.gInvalid = createEvent(this, "g-invalid", 7);
        this.valid = false;
        this.open = false;
        this.floatingLabel = undefined;
        this.floatingLabelState = undefined;
        this.label = undefined;
        this.labelFor = undefined;
        this.hideLabel = false;
        this.error = false;
        this.type = undefined;
        this.tooltip = undefined;
        this.tooltipOpen = false;
        this.required = false;
        this.pattern = undefined;
        this.withButton = false;
        this.buttonTitle = 'Submit';
        this.buttonTheme = 'dark';
        this.dataTestId = undefined;
    }
    /* onClick handler for appended button */
    onClick(event) {
        var _a;
        (_a = this.gClickAppend) === null || _a === void 0 ? void 0 : _a.emit(event);
    }
    /* onClick handler for appended button */
    onFloatingLabelClick(event) {
        var _a;
        (_a = this.gClickFloatingLabel) === null || _a === void 0 ? void 0 : _a.emit(event);
    }
    onClickTooltip(event) {
        var _a, _b;
        if (this.open) {
            (_a = this.gClose) === null || _a === void 0 ? void 0 : _a.emit(event);
        }
        else {
            (_b = this.gOpen) === null || _b === void 0 ? void 0 : _b.emit(event);
        }
        this.open = !this.open;
    }
    onKeyDown(event) {
        var _a, _b;
        /**
         * Listen for Enter/Return and Spacebar keys
         */
        if (event.key === 'Enter' || event.key === ' ') {
            if (this.open) {
                (_a = this.gClose) === null || _a === void 0 ? void 0 : _a.emit(event);
            }
            else {
                (_b = this.gOpen) === null || _b === void 0 ? void 0 : _b.emit(event);
            }
            this.open = !this.open;
        }
    }
    inputHandler(event) {
        var _a, _b;
        const input = event.target;
        const { validity } = input;
        if (validity.valueMissing) {
            this.valid = false;
        }
        else {
            if (this.type) {
                this.valid = !validity.typeMismatch;
            }
            if (this.pattern) {
                this.valid = !validity.patternMismatch;
            }
        }
        if (this.valid) {
            (_a = this.gValid) === null || _a === void 0 ? void 0 : _a.emit(event);
        }
        else {
            (_b = this.gInvalid) === null || _b === void 0 ? void 0 : _b.emit(event);
        }
    }
    componentWillLoad() {
        this.open = this.tooltipOpen;
    }
    componentWillRender() {
        if (!this.label) {
            /* eslint-disable */
            console.error('No label attribute set on g-input component');
        }
        if (!this.labelFor) {
            /* eslint-disable */
            console.error('No label-for attribute set on g-input component');
        }
    }
    append() {
        var _a, _b, _c;
        if (this.withButton) {
            return (h("div", { class: "g__input__button-wrapper", "data-testid": (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.buttonWrapper }, h("g-button", { disabled: this.buttonTheme === 'disabled', label: this.buttonTitle, theme: this.buttonTheme, dataTestId: (_b = this.dataTestId) === null || _b === void 0 ? void 0 : _b.gButton, onClick: (e) => this.onClick(e) }, h("span", { class: "g__input__button--append__icon", "data-testid": (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.arrowRight, "aria-hidden": "true", innerHTML: ArrowRight }))));
        }
        return (h("slot", { name: "append" }));
    }
    renderLabel() {
        var _a;
        return (h(Fragment, null, this.label, this.required && (h("span", { class: "g__input__required", "data-testid": (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.required }, "*"))));
    }
    renderFloatingLabel() {
        var _a, _b, _c;
        if (this.floatingLabelState === 'loading') {
            return (h("div", { "data-testid": (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.loaderWrapper, style: { 'width': '40px' } }, h("g-loader", { dataTestId: (_b = this.dataTestId) === null || _b === void 0 ? void 0 : _b.gLoader, type: "dark" })));
        }
        return (h("button", { type: "button", "data-testid": (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.button, onClick: (e) => this.onFloatingLabelClick(e) }, this.floatingLabel));
    }
    renderTooltipLabel() {
        var _a, _b, _c, _d;
        return (h("details", { class: "g__input__label__details", "data-testid": (_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.details, open: this.tooltipOpen, onClick: (e) => this.onClickTooltip(e), onKeyDown: (e) => this.onKeyDown(e) }, h("summary", { class: "g__input__label__summary", "data-testid": (_b = this.dataTestId) === null || _b === void 0 ? void 0 : _b.summary }, this.renderLabel(), h("svg", { class: "g__input__label__summary__icon", "data-testid": (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.icon, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 100 100", role: "presentation" }, h("circle", { role: "presentation", cx: "49.8", cy: "50.2", r: "48.8", fill: "#7a7a7a" }), h("path", { role: "presentation", d: "M45.3 60.2v-2.5c0-2.2.5-4.1 1.4-5.7.9-1.6 2.6-3.3 5.1-5.2 2.4-1.7 4-3.1 4.7-4.2.8-1.1 1.1-2.3 1.1-3.6 0-1.5-.5-2.6-1.6-3.4-1.1-.8-2.6-1.2-4.6-1.2-3.4 0-7.3 1.1-11.7 3.3L36 30.4c5.1-2.8 10.5-4.3 16.2-4.3 4.7 0 8.4 1.1 11.2 3.4s4.2 5.3 4.2 9c0 2.5-.6 4.7-1.7 6.5-1.1 1.8-3.3 3.9-6.5 6.2-2.2 1.6-3.6 2.8-4.2 3.7-.6.8-.9 1.9-.9 3.3v2.1h-9zm-1.1 11.7c0-1.9.5-3.4 1.5-4.3 1-1 2.5-1.5 4.5-1.5 1.9 0 3.4.5 4.4 1.5 1 1 1.6 2.4 1.6 4.3 0 1.8-.5 3.2-1.6 4.3-1 1-2.5 1.6-4.4 1.6-1.9 0-3.4-.5-4.4-1.5-1.1-1.1-1.6-2.5-1.6-4.4z", fill: "#ffffff" }))), h("span", { class: "g__input__label__summary__copy", "data-testid": (_d = this.dataTestId) === null || _d === void 0 ? void 0 : _d.tooltip }, this.tooltip)));
    }
    render() {
        var _a, _b, _c;
        const baseClass = 'g__input';
        const componentClasses = {
            [baseClass]: true,
            [`${baseClass}--error`]: Boolean(this.error),
            [`${baseClass}--with-button`]: Boolean(this.withButton),
            [`${baseClass}--with-floating-label`]: Boolean(this.floatingLabel),
            [`${baseClass}--with-floating-label--${this.floatingLabelState || ''}`]: Boolean(this.floatingLabelState)
        };
        const labelTextClasses = {
            g__input__label__text: true,
            'g__input__label__text--hidden': Boolean(this.hideLabel),
        };
        return (h(Host, { key: '27060e5c5023f4803bf069660e0b70bb600aff0c', class: componentClasses, "data-testid": ((_a = this.dataTestId) === null || _a === void 0 ? void 0 : _a.host) || baseClass }, h("label", { key: '94285b8fe5ca6207e853463edf2e8e4d09d35b3b', class: "g__input__label", htmlFor: this.labelFor, "data-testid": (_b = this.dataTestId) === null || _b === void 0 ? void 0 : _b.label }, h("span", { key: '786d21c0946a11dc961ff0f5a053de59ad3109d5', class: labelTextClasses }, this.tooltip ? this.renderTooltipLabel() : this.renderLabel())), h("div", { key: 'd015b08503249cd3431dd3a87b45a72929d390e7', class: "g__input__wrapper", "data-testid": (_c = this.dataTestId) === null || _c === void 0 ? void 0 : _c.wrapper, onInput: (e) => this.inputHandler(e) }, h("slot", { key: '6449d4329f72a59e53208eb503fdd87c954f26bf', name: "prepend" }), h("slot", { key: '41da4aca8590108b3b3ce46a5b358a502ef032db' }), this.floatingLabel ? this.renderFloatingLabel() : '', this.append())));
    }
    static get style() { return GInputStyle0; }
}, [4, "g-input", {
        "floatingLabel": [1, "floating-label"],
        "floatingLabelState": [1, "floating-label-state"],
        "label": [1],
        "labelFor": [1, "label-for"],
        "hideLabel": [4, "hide-label"],
        "error": [4],
        "type": [1],
        "tooltip": [1],
        "tooltipOpen": [4, "tooltip-open"],
        "required": [4],
        "pattern": [1],
        "withButton": [4, "with-button"],
        "buttonTitle": [1, "button-title"],
        "buttonTheme": [513, "button-theme"],
        "dataTestId": [1040],
        "valid": [32],
        "open": [32]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["g-input", "g-button", "g-loader"];
    components.forEach(tagName => { switch (tagName) {
        case "g-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, GInput$1);
            }
            break;
        case "g-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "g-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const GInput = GInput$1;
const defineCustomElement = defineCustomElement$1;

export { GInput, defineCustomElement };

//# sourceMappingURL=g-input.js.map